import React from 'react';
import {
  Box,
  Container,
  Grid,
  Paper,
  Stack,
  Typography,
  Button
} from '@mui/material';
import { Email, Phone } from '@mui/icons-material';
import { Link } from 'react-router-dom';

const policySections = [
  {
    heading: 'Information We Collect',
    items: [
      'Personal Information: When you visit our website, place an order, or communicate with us, we may collect details such as your name, email address, shipping address, billing address, phone number, and payment information.',
      'Usage Information: We also gather non-personal browsing data, including IP address, browser type, operating system, and pages visited.'
    ]
  },
  {
    heading: 'How We Use Your Information',
    items: [
      'We process personal information to fulfil orders, support customers, and respond to enquiries.',
      'Where permitted by law, we use your information to improve our site, personalise experiences, and send marketing communications about our products and promotions.'
    ]
  },
  {
    heading: 'Information Sharing and Disclosure',
    items: [
      'Your information may be shared with trusted service providers who assist with website operations, payment processing, logistics, and similar services.',
      'We may disclose information to comply with legal obligations, protect our rights or property, or prevent fraud and illegal activities.'
    ]
  },
  {
    heading: 'Data Security',
    items: [
      'We employ reasonable safeguards to maintain the security and accuracy of your personal information. However, no method of transmission over the internet or electronic storage is completely secure.'
    ]
  },
  {
    heading: 'Your Choices',
    items: [
      'You may access, update, or request deletion of your personal information by contacting us using the details below.',
      'To opt out of marketing emails, follow the unsubscribe instructions contained in our communications or contact us directly.'
    ]
  },
  {
    heading: "Children's Privacy",
    items: [
      'Our website is not intended for children under the age of 13, and we do not knowingly collect personal information from children.'
    ]
  },
  {
    heading: 'Changes to This Policy',
    items: [
      'We may update this Privacy Policy periodically to reflect changes in our practices or applicable laws. Material updates will be posted on this page.'
    ]
  },
  {
    heading: 'Contact Us',
    items: [
      'If you have questions or concerns about this Privacy Policy or our data practices, please contact us at info@zaror.co.uk.'
    ]
  }
];

const ContactCTA = () => (
  <Box
    sx={{
      backgroundImage:
        'linear-gradient(135deg, rgba(0,0,0,0.88) 0%, rgba(34,34,34,0.9) 100%)',
      color: 'white',
      py: { xs: 8, md: 10 },
      mt: 8
    }}
  >
    <Container maxWidth="lg">
      <Typography variant="h6" sx={{ mb: 2, textTransform: 'uppercase', letterSpacing: 1 }}>
        Contacts
      </Typography>
      <Typography variant="h3" component="h2" sx={{ fontWeight: 700, mb: 4 }}>
        For any inquiries please email or call us
      </Typography>
      <Grid container spacing={4} alignItems="center">
        <Grid item xs={12} md={6}>
          <Stack spacing={3}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Email sx={{ fontSize: 32, color: 'secondary.main' }} />
              <Typography
                component="a"
                href="mailto:info@zaror.co.uk"
                variant="h5"
                sx={{ color: 'white', textDecoration: 'none', fontWeight: 600 }}
              >
                info@zaror.co.uk
              </Typography>
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center">
              <Phone sx={{ fontSize: 32, color: 'secondary.main' }} />
              <Typography
                component="a"
                href="tel:08006351020"
                variant="h5"
                sx={{ color: 'white', textDecoration: 'none', fontWeight: 600 }}
              >
                0800 635 1020
              </Typography>
            </Stack>
            <Stack direction="row" spacing={2} alignItems="center">
              <Phone sx={{ fontSize: 32, color: 'secondary.main' }} />
              <Typography
                component="a"
                href="tel:01216901020"
                variant="h5"
                sx={{ color: 'white', textDecoration: 'none', fontWeight: 600 }}
              >
                0121 690 1020
              </Typography>
            </Stack>
          </Stack>
        </Grid>
        <Grid item xs={12} md={6}>
          <Paper
            elevation={6}
            sx={{
              p: { xs: 4, md: 5 },
              borderRadius: 3,
              backgroundColor: 'rgba(255,255,255,0.05)',
              border: '1px solid rgba(255,255,255,0.12)'
            }}
          >
            <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
              Ready to discuss your kit?
            </Typography>
            <Typography variant="body1" sx={{ mb: 3, opacity: 0.8 }}>
              Send us your requirements and our team will be in touch with concepts,
              pricing, and next steps.
            </Typography>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <Button
                component={Link}
                to="/contact"
                variant="contained"
                color="secondary"
                sx={{ fontWeight: 600, borderRadius: 2 }}
              >
                Contact Us
              </Button>
            </Stack>
          </Paper>
        </Grid>
      </Grid>
    </Container>
  </Box>
);

const PrivacyPolicy = () => {
  return (
    <Box>
      <Box sx={{ backgroundColor: '#0f172a', color: 'white', py: { xs: 10, md: 12 } }}>
        <Container maxWidth="lg">
          <Stack spacing={2} alignItems={{ xs: 'center', md: 'flex-start' }}>
            <Typography variant="h4" sx={{ letterSpacing: 6, textTransform: 'uppercase' }}>
              Privacy Policy
            </Typography>
            <Typography
              variant="h2"
              component="h1"
              sx={{ fontWeight: 700, textAlign: { xs: 'center', md: 'left' } }}
            >
              Your privacy matters to us
            </Typography>
            <Typography
              variant="h6"
              sx={{
                maxWidth: 720,
                opacity: 0.85,
                textAlign: { xs: 'center', md: 'left' }
              }}
            >
              Zaror Sports is committed to protecting the privacy and security of your personal information. This policy outlines how information is collected, used, and safeguarded when you interact with us.
            </Typography>
          </Stack>
        </Container>
      </Box>

      <Container maxWidth="lg" sx={{ py: { xs: 6, md: 8 } }}>
        <Stack spacing={4}>
          {policySections.map((section) => (
            <Paper key={section.heading} elevation={2} sx={{ p: { xs: 3, md: 4 }, borderRadius: 3 }}>
              <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
                {section.heading}
              </Typography>
              <Stack spacing={1.5}>
                {section.items.map((item, index) => (
                  <Typography key={index} variant="body1" color="text.secondary">
                    {item}
                  </Typography>
                ))}
              </Stack>
            </Paper>
          ))}
        </Stack>
      </Container>

      <ContactCTA />

      <Box sx={{ py: 4, textAlign: 'center', bgcolor: 'background.default' }}>
        <Typography variant="body2" color="text.secondary">
          © {new Date().getFullYear()} All Rights Reserved.
        </Typography>
      </Box>
    </Box>
  );
};

export default PrivacyPolicy;
